import { useCallback } from 'react'
import { useAuth } from 'context/authentication/useAuth'
import { Outlet, useSearchParams } from 'react-router-dom'
import { UnifiedSearchModal } from 'components/UnifiedSearch'
import {
  LeftNavBar,
  SlideOutPanel,
  useLeftNavStore,
  NavItemsProvider,
} from 'components/Layout/LeftNav'
import { Header } from 'components/Layout/Header'
import { NewFooter } from 'components/Layout/Footer/NewFooter'
import { LayoutConfigProvider, useLayoutConfig } from 'context/layout'
import { cn } from 'components/UI/cn'

export function AppLayout() {
  return (
    <LayoutConfigProvider>
      <NavItemsProvider>
        <AppLayoutContent />
      </NavItemsProvider>
    </LayoutConfigProvider>
  )
}

function AppLayoutContent() {
  const { isAuthenticated } = useAuth()
  const { isCollapsed, isSlideOutOpen, activeSection } = useLeftNavStore()
  const [, setSearchParams] = useSearchParams()
  const { config } = useLayoutConfig()

  const openSearch = useCallback(() => {
    setSearchParams((prev) => {
      prev.set('search', 'true')
      return prev
    })
  }, [setSearchParams])

  const showSlideOut = isAuthenticated && isSlideOutOpen && activeSection

  const hasLeftNav = isAuthenticated && !isCollapsed

  return (
    <div className="flex flex-col h-screen w-full overflow-hidden bg-[var(--nav-bg-primary)]">
      {/* Top Header - spans full width */}
      {isAuthenticated && <Header onSearchClick={openSearch} />}

      {/* Main Area: Left Nav + Content */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left Navigation Bar */}
        {isAuthenticated && <LeftNavBar />}

        {/* App Shell with border effect */}
        <div
          className={cn(
            'flex flex-row flex-1 min-w-0 m-[var(--app-border-width)] bg-surface-sunken rounded-xl overflow-hidden',
            hasLeftNav ? 'ml-0' : 'ml-[var(--app-border-width)]'
          )}
        >
          {/* Slide-Out Panel - inside AppShell, pushes content */}
          {isAuthenticated && <SlideOutPanel $isOpen={!!showSlideOut} />}

          <div className="flex flex-col flex-1 min-w-0">
            <div
              id="app-content-container"
              className="flex-1 flex flex-col overflow-auto"
            >
              <Outlet />
            </div>
            {/* Hide footer on pages that request it (e.g., company pages) */}
            {!config.hideFooter && <NewFooter />}
          </div>
        </div>
      </div>

      {isAuthenticated && <UnifiedSearchModal />}
    </div>
  )
}
